import { useEffect, useState } from "react";
import {
    DOC_OPCIONES,
    DOC_OPCIONES_FACTURA,
    FacturacionForm,
    TipoComprobante,
    TipoDocSunat,
} from "./facturacion_form.types";
import Swal from "sweetalert2";

const CONTROL =
    "w-full h-10 px-3 rounded-lg border border-[#E9E9E9] bg-white outline-none text-sm focus:ring-2 focus:ring-[#1C4CBB]/40 focus:border-[#1C4CBB]";

/* ---------------------------- modal ---------------------------- */

export const ModalDatosComprobante: React.FC<{
    visible: boolean;
    onClose: () => void;
    initial?: Partial<FacturacionForm>;
    onSave: (data: FacturacionForm) => void;
}> = ({ visible, onClose, initial, onSave }) => {
    const [form, setForm] = useState<FacturacionForm>({
        tipo_comprobante: "Factura",
        correo: "",
        correo_copia: "",
        direccion: "",
        ubigeo: "",
        nombre_comercial: "",
        nombre_legal: "",
        tipo_documento_identidad: "RUC",
        numero_documento_identidad: "",
        ...initial,
    });

    useEffect(() => {
        if (visible && initial) setForm((p) => ({ ...p, ...initial }));
    }, [visible, initial]);

    const EMAIL = /^\S+@\S+\.\S+$/;

    const validate = () => {
        if (!form.tipo_comprobante)
            return "Debes seleccionar Factura o Boleta.";
        if (!EMAIL.test(form.correo)) return "Correo inválido.";
        if (form.correo_copia && !EMAIL.test(form.correo_copia))
            return "Correo copia inválido.";
        if (!form.direccion.trim()) return "Dirección requerida.";
        if (!form.ubigeo.trim()) return "Ubigeo requerido.";
        if (!form.nombre_comercial.trim()) return "Nombre comercial requerido.";
        if (!form.nombre_legal.trim()) return "Nombre legal requerido.";
        const ndoc = form.numero_documento_identidad.trim();
        switch (form.tipo_documento_identidad) {
            case "RUC":
                if (!/^\d{11}$/.test(ndoc)) return "RUC inválido (11 dígitos).";
                break;
            case "DOC_NACIONAL_DE_IDENTIDAD":
                if (!/^\d{8}$/.test(ndoc)) return "DNI inválido (8 dígitos).";
                break;
            default:
                if (!/^[A-Za-z0-9\-]{6,}$/.test(ndoc))
                    return "Número de documento inválido (mín. 6 caracteres).";
        }
        return null;
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl p-4 bg-white rounded-xl">
                <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold">
                        Datos para el comprobante
                    </h3>
                    <button
                        className="px-3 py-1 text-sm text-[#1C4CBB] border border-[#1C4CBB] rounded-lg"
                        onClick={onClose}
                    >
                        Cerrar
                    </button>
                </div>
                <hr className="my-3" />

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            Tipo de comprobante *
                        </label>
                        <select
                            className={CONTROL}
                            value={form.tipo_comprobante}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    tipo_comprobante: e.target
                                        .value as TipoComprobante,
                                    tipo_documento_identidad:
                                        e.target.value === "Boleta"
                                            ? "DOC_TRIB_NO_DOM_SIN_RUC"
                                            : "RUC",
                                }))
                            }
                        >
                            <option value="Factura">Factura</option>
                            <option value="Boleta">Boleta</option>
                        </select>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            Tipo de documento *
                        </label>
                        <select
                            className={CONTROL}
                            value={form.tipo_documento_identidad}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    tipo_documento_identidad: e.target
                                        .value as TipoDocSunat,
                                    numero_documento_identidad:
                                        e.target.value === "RUC"
                                            ? p.numero_documento_identidad
                                                  .replace(/\D/g, "")
                                                  .slice(0, 11)
                                            : p.numero_documento_identidad,
                                }))
                            }
                        >
                            {form.tipo_comprobante === "Factura"
                                ? DOC_OPCIONES_FACTURA.map((o) => (
                                      <option key={o.value} value={o.value}>
                                          {o.label}
                                      </option>
                                  ))
                                : DOC_OPCIONES.map((o) => (
                                      <option key={o.value} value={o.value}>
                                          {o.label}
                                      </option>
                                  ))}
                        </select>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            {form.tipo_documento_identidad === "RUC"
                                ? "RUC *"
                                : "Nro. documento *"}
                        </label>
                        <input
                            className={CONTROL}
                            value={form.numero_documento_identidad}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (form.tipo_documento_identidad === "RUC")
                                    return setForm((p) => ({
                                        ...p,
                                        numero_documento_identidad: v
                                            .replace(/\D/g, "")
                                            .slice(0, 11),
                                    }));
                                setForm((p) => ({
                                    ...p,
                                    numero_documento_identidad: v.slice(0, 20),
                                }));
                            }}
                            placeholder={
                                form.tipo_documento_identidad === "RUC"
                                    ? "2061********"
                                    : "Documento del cliente"
                            }
                            inputMode={
                                form.tipo_documento_identidad === "RUC" ||
                                form.tipo_documento_identidad ===
                                    "DOC_NACIONAL_DE_IDENTIDAD"
                                    ? "numeric"
                                    : "text"
                            }
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Ubigeo *</label>
                        <input
                            className={CONTROL}
                            value={form.ubigeo}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    ubigeo: e.target.value,
                                }))
                            }
                            placeholder="Ej: 591"
                        />
                    </div>

                    <div className="flex flex-col gap-1 sm:col-span-2">
                        <label className="text-sm font-medium">
                            Dirección *
                        </label>
                        <input
                            className={CONTROL}
                            value={form.direccion}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    direccion: e.target.value,
                                }))
                            }
                            placeholder="Av. Siempre Viva 123"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            Nombre comercial *
                        </label>
                        <input
                            className={CONTROL}
                            value={form.nombre_comercial}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    nombre_comercial: e.target.value,
                                }))
                            }
                            placeholder="Mi Empresa / Nombres y Apellidos"
                        />
                    </div>

                    <div className="flex flex-col gap-1 sm:col-span-2">
                        <label className="text-sm font-medium">
                            Nombre legal *
                        </label>
                        <input
                            className={CONTROL}
                            value={form.nombre_legal}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    nombre_legal: e.target.value,
                                }))
                            }
                            placeholder="Razón social o Nombres y Apellidos"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Correo *</label>
                        <input
                            type="email"
                            className={CONTROL}
                            value={form.correo}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    correo: e.target.value,
                                }))
                            }
                            placeholder="facturacion@empresa.com"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">
                            Correo copia (opcional)
                        </label>
                        <input
                            type="email"
                            className={CONTROL}
                            value={form.correo_copia ?? ""}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    correo_copia: e.target.value,
                                }))
                            }
                            placeholder="copia@empresa.com"
                        />
                    </div>
                </div>

                <div className="flex justify-center mt-4">
                    <button
                        className="h-10 px-6 text-white rounded-lg bg-[#1C4CBB]"
                        onClick={() => {
                            const err = validate();
                            if (err) {
                                Swal.fire({
                                    confirmButtonColor: "#029BFC",
                                    iconColor: "#029BFC",
                                    title: "Dato inválido",
                                    text: err,
                                    icon: "error",
                                });
                                return;
                            }
                            onSave(form);
                        }}
                    >
                        Guardar datos
                    </button>
                </div>
            </div>
        </div>
    );
};
